import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { BiBuildings, BiTable, BiCodeCurly, BiFileBlank } from 'react-icons/bi';
import { FaMask, FaLock } from 'react-icons/fa';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import AppContext from 'Utils/AppContext';
import { STATUS, INK } from 'Utils/styleTokens';
import { listMaskingJobs, listEncryptJobs, listDecryptJobs } from 'Secure/api';
import { getEnterpriseSummary } from '../edlApi';
import { SEVERITY_STYLE, DATA_CLASS_LABELS, sizeTiles, queueCount, sourceTypeLabel } from '../edlUtils';
import {
  SOURCE_STATE, STATE_LABELS, STATE_HELP, STATE_PILL, isManaged, protectionFromTags,
  deriveProtectionByDataSource,
} from '../stateTag';

const LEGEND = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'CLEAN', 'NEVER_SCANNED'];
const CLASS_CHIPS = [['ALL', 'All classes'], ['STRUCTURED', 'Structured'], ['SEMI_STRUCTURED', 'Semi-structured'], ['UNSTRUCTURED', 'Unstructured']];
// Icon badge per data class on every tile. There is no background pattern any more — the tiles
// carry only ONE pattern now, the diagonal stripe that means "managed" — so the class key that
// explained "icon + pattern" is gone too; the class chips below the grid say the same thing.
const CLASS_ICON = { STRUCTURED: BiTable, SEMI_STRUCTURED: BiCodeCurly, UNSTRUCTURED: BiFileBlank };
const ClassBadge = ({ dataClass }) => {
  const Icon = CLASS_ICON[dataClass] || BiFileBlank;
  return (
    <span className='edl-class-badge' title={`${DATA_CLASS_LABELS[dataClass] || dataClass} data`}>
      <Icon size='12px' /><span className='edl-class-badge__text'>{DATA_CLASS_LABELS[dataClass] || dataClass}</span>
    </span>
  );
};
const QUEUE_SIZE = 6;
const POLL_MS = 30000;
// How far back the protection badges look. Same page size the detail page uses for the same
// three lists, so a tile and that source's own page never disagree about what is protected.
const PROTECTION_JOB_PAGE = 200;

// Lower-right of a tile: this source has been secured. It is deliberately independent of the
// tile's severity colour, which keeps saying what the last scan FOUND — masking does not delete
// the findings, it makes them harmless, and the two facts are worth seeing together.
const PROTECTION_ICON = { [SOURCE_STATE.MASKED]: FaMask, [SOURCE_STATE.ENCRYPTED]: FaLock };
const ProtectionBadge = ({ state, sourceId }) => {
  const Icon = PROTECTION_ICON[state] || FaLock;
  const pill = STATE_PILL[state] || {};
  return (
    <span
      className='edl-tile__protection'
      style={{ background: pill.bg, color: pill.fg }}
      data-testid={`edl-protection-${sourceId}`}
      title={STATE_HELP[state]}
    >
      <Icon size='9px' />
      <span className='edl-tile__protection-text'>{STATE_LABELS[state]}</span>
    </span>
  );
};

const DiffItem = ({ arrow, value, label }) => (
  <span className='edl-tile__diff-item'>
    <span className='edl-tile__diff-value'>{arrow}{value.toLocaleString()}</span>
    <span className='edl-tile__diff-label'>{label}</span>
  </span>
);

/**
 * The three movement numbers in a tile's bottom-left, in place of the single finding total. They
 * are the source's own share of the "Risk Profile Changes Since Last Scan" strip at the top of
 * the page, so the tiles add up to it — one big number told you how much there was, these tell
 * you which way it is going.
 *
 * The unit is FIELDS: distinct columns, NoSQL fields or files. A source scanned only once still
 * has three numbers — its baseline, where nothing is resolved or persisting and all of it is new.
 */
const TileDiff = ({ diff }) => (
  <div
    className='edl-tile__diff'
    data-testid='edl-tile-diff'
    title={`${diff.resolved.toLocaleString()} resolved · ${diff.persisting.toLocaleString()} persisting · ${diff.new.toLocaleString()} new — fields (columns, NoSQL fields, files) since the previous run`}
  >
    <DiffItem arrow='↓' value={diff.resolved} label='resolved' />
    <DiffItem value={diff.persisting} label='persisting' />
    <DiffItem arrow='↑' value={diff.new} label='new' />
  </div>
);

/**
 * A scanned source the platform could not put a number on — the two runs ran on different
 * workers, or the worker could not be reached. It says so instead of borrowing the entity
 * instance total, which is a different unit and used to read as a colossal finding count.
 */
const TileNotCompared = ({ reason }) => (
  <div className='edl-tile__state edl-tile__state--muted' data-testid='edl-tile-uncompared'
    title={reason || 'The last two runs could not be compared'}>
    not compared
  </div>
);

const Tile = ({ source, span, onOpen }) => {
  const style = SEVERITY_STYLE[source.severity] || SEVERITY_STYLE.NEVER_SCANNED;
  // A never-scanned source HAS no findings to drill into, but it is exactly the tile someone
  // clicks wanting to do something about it — so it opens the Data Source Risk Profile, where
  // discovery is started, instead of being a dead square (see `open`).
  const neverScanned = source.severity === 'NEVER_SCANNED';
  const classes = ['edl-tile', `edl-tile--${span}`, 'edl-tile--clickable'];
  if (style.dashed) classes.push('edl-tile--dashed');
  if (source.severity === 'CLEAN' || source.severity === 'NEVER_SCANNED') classes.push('edl-tile--light');
  // The one remaining pattern: 45° stripes = this source is managed by hand.
  if (source.managed) classes.push('edl-tile--managed');
  const notes = [];
  if (source.managed) notes.push(STATE_LABELS[SOURCE_STATE.MANAGED]);
  if (source.protection) notes.push(STATE_LABELS[source.protection]);
  const stateNote = notes.length ? ` · ${notes.join(' · ')}` : '';
  return (
    <button
      type='button'
      className={classes.join(' ')}
      style={{ backgroundColor: style.bg, color: style.fg }}
      data-class={source.dataClass}
      onClick={() => onOpen(source)}
      data-testid={`edl-tile-${source.id}`}
      data-severity={source.severity}
      data-managed={source.managed ? 'true' : 'false'}
      data-protection={source.protection || ''}
      title={neverScanned
        ? `${source.name} · ${style.label}${stateNote} — open its risk profile to run discovery`
        : `${source.name} · ${style.label}${stateNote}`}
    >
      <div className='edl-tile__head'>
        <div className='edl-tile__name'>{source.name}</div>
        <ClassBadge dataClass={source.dataClass} />
      </div>
      <div className='edl-tile__sub'>
        {sourceTypeLabel(source)}
        {source.managed && <span className='edl-tile__managed-note'> · managed</span>}
      </div>
      <div className='edl-tile__foot-row'>
        <div className='edl-tile__foot-main'>
          {neverScanned && <div className='edl-tile__state'>? never scanned</div>}
          {source.severity === 'CLEAN' && <div className='edl-tile__state'>✓ clean</div>}
          {!neverScanned && source.severity !== 'CLEAN' && (
            source.diff
              ? <TileDiff diff={source.diff} />
              : <TileNotCompared reason={source.diffError} />
          )}
        </div>
        {source.protection && <ProtectionBadge state={source.protection} sourceId={source.id} />}
      </div>
    </button>
  );
};

// Enterprise Data Landscape home: since-last-scan strip, severity-coloured tile grid sized by
// finding volume, and the open-findings remediation queue. One summary call, polled like the SDL.
const Home = () => {
  const history = useHistory();
  const { connections } = useContext(AppContext);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classFilter, setClassFilter] = useState('ALL');
  // Legend swatches double as severity toggles: empty = every severity; otherwise only the
  // listed ones. Filters the tile grid AND the remediation queue.
  const [severityFilter, setSeverityFilter] = useState([]);
  // The managed key in the same legend row is a filter too: on = only the hand-managed sources.
  const [managedOnly, setManagedOnly] = useState(false);
  const [showAllQueue, setShowAllQueue] = useState(false);
  // data-source id → MASKED / ENCRYPTED, from the masking and encrypt/decrypt job lists. null
  // while it has not loaded (or none of the three lists could be read), which is what makes the
  // stored `state` tag the fallback below rather than a second opinion.
  const [protectionById, setProtectionById] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const s = await getEnterpriseSummary();
        if (!alive) return;
        setSummary(s);
        setError(null);
      } catch (e) {
        if (alive) setError(e?.message || 'Failed to load the Enterprise Data Landscape');
      } finally {
        if (alive) setLoading(false);
      }
    };
    // Protection is derived here rather than read off the `state` tag alone: the tag is only
    // rewritten when someone opens that source's detail page, so a source masked this morning
    // would sit on the grid unbadged until a human happened to visit it.
    const loadProtection = async () => {
      const [maskRes, encRes, decRes] = (await Promise.allSettled([
        listMaskingJobs({ page: 0, size: PROTECTION_JOB_PAGE }),
        listEncryptJobs({ page: 0, size: PROTECTION_JOB_PAGE }),
        listDecryptJobs({ page: 0, size: PROTECTION_JOB_PAGE }),
      ])).map((r) => (r.status === 'fulfilled' ? r.value : null));
      if (!alive) return;
      // All three failed: keep whatever we had and let the tag fallback answer, rather than
      // un-badging every protected source because the job service blinked.
      if (maskRes == null && encRes == null && decRes == null) return;
      setProtectionById(deriveProtectionByDataSource(
        maskRes?.content || [], encRes?.content || [], decRes?.content || []
      ));
    };
    const loadAll = () => { load(); loadProtection(); };
    loadAll();
    const timer = setInterval(loadAll, POLL_MS);
    return () => { alive = false; clearInterval(timer); };
  }, []);

  // The `state` tag rides on the data-source DTO, which the app already has globally — so the grid
  // can lay itself out by state without a second request per source. The EDL summary itself has
  // no tags.
  const managedIds = useMemo(() => {
    const ids = new Set();
    (connections || []).forEach((c) => {
      if (c && c.id != null && isManaged(c.tags)) ids.add(String(c.id));
    });
    return ids;
  }, [connections]);
  // Fallback for the badges while the job lists are loading, or if they cannot be read at all:
  // the state the detail page last stored on the source. Stale by construction — the live
  // derivation replaces it wholesale the moment it arrives.
  const protectionByTag = useMemo(() => {
    const map = {};
    (connections || []).forEach((c) => {
      if (!c || c.id == null) return;
      const state = protectionFromTags(c.tags);
      if (state) map[String(c.id)] = state;
    });
    return map;
  }, [connections]);
  const protection = protectionById || protectionByTag;
  const sources = useMemo(
    () => (summary?.sources || []).map((s) => ({
      ...s,
      managed: managedIds.has(String(s.id)),
      protection: protection[String(s.id)] || null,
    })),
    [summary, managedIds, protection]
  );
  const managedCount = sources.filter((s) => s.managed).length;
  const bySeverity = useMemo(() => {
    const bySev = severityFilter.length === 0
      ? sources
      : sources.filter((s) => severityFilter.includes(s.severity));
    return managedOnly ? bySev.filter((s) => s.managed) : bySev;
  }, [sources, severityFilter, managedOnly]);
  const filtered = useMemo(
    () => (classFilter === 'ALL' ? bySeverity : bySeverity.filter((s) => s.dataClass === classFilter)),
    [bySeverity, classFilter]
  );
  const tiles = useMemo(() => sizeTiles(filtered), [filtered]);
  const fullQueue = useMemo(() => bySeverity
    .filter((s) => s.severity !== 'NEVER_SCANNED' && queueCount(s) > 0)
    .sort((a, b) => queueCount(b) - queueCount(a)), [bySeverity]);
  const toggleSeverity = (sev) => {
    setSeverityFilter((prev) => (prev.includes(sev) ? prev.filter((x) => x !== sev) : [...prev, sev]));
  };
  const queue = showAllQueue ? fullQueue : fullQueue.slice(0, QUEUE_SIZE);
  const queueMax = queue.length ? queueCount(queue[0]) : 0;
  const since = summary?.sinceLastScan || { resolved: 0, persisting: 0, new: 0 };
  // Every source with a completed, comparable run — a one-run source has a baseline diff now.
  const diffed = sources.filter((s) => s.diff).length;

  // Never scanned → the Data Source Risk Profile, which is where discovery is started; anything
  // else → the "where inside this source" page, which needs a completed run to say anything.
  const open = (source) => history.push(source.severity === 'NEVER_SCANNED'
    ? `/landscape/riskprofile/${encodeURIComponent(source.id)}`
    : `/enterprise/source/${source.id}`);

  return (
    <FetchContainer fetchingData={loading} absolute={true} error={error}>
      <div className='edl-page'>
        <PageHeader
          icon={BiBuildings}
          eyebrow='Enterprise estate'
          header='Enterprise Data Landscape'
          tooltip='Where the risk is sitting across shares, buckets, SharePoint and SFTP, and whether it has been cleaned up.'
        />

        <div className='edl-card' data-testid='edl-since-last-scan'>
          <div className='sg-eyebrow' style={{ marginBottom: 12 }}>Risk Profile Changes Since Last Scan</div>
          <div className='edl-since'>
            <div className='edl-since__tile' style={{ background: STATUS.success.bg, color: STATUS.success.text }}>
              <div className='edl-since__big'>↓ {since.resolved.toLocaleString()}</div>
              <div className='edl-since__label'>resolved</div>
            </div>
            <div className='edl-since__tile' style={{ background: STATUS.warning.bg, color: STATUS.warning.text }}>
              <div className='edl-since__big'>{since.persisting.toLocaleString()}</div>
              <div className='edl-since__label'>persisting</div>
            </div>
            <div className='edl-since__tile' style={{ background: STATUS.danger.bg, color: STATUS.danger.text }}>
              <div className='edl-since__big'>↑ {since.new.toLocaleString()}</div>
              <div className='edl-since__label'>new</div>
            </div>
          </div>
          <div className='sg-small' style={{ marginTop: 10, color: INK[400] }}>
            {diffed === 0
              ? 'Appears once a source has a completed discovery run.'
              : `The sum of all ${diffed} scanned source${diffed === 1 ? '' : 's'} below · counts are fields`
                + ' (columns, NoSQL fields, files) · a source scanned once counts as all new.'}
          </div>
        </div>

        <div className='edl-card' style={{ padding: 18, overflowX: 'auto' }}>
          {/* Risk legend first (it doubles as the risk filter), then the data-class filter under
              it — the two filters read as one stack instead of one sitting up in the page title. */}
          <div className='edl-legend' role='group' aria-label='Filter by risk' style={{ marginBottom: 10 }}>
            {/* Same chip as "All classes" in the row below, so the two filters read as a pair. */}
            <button
              type='button'
              className={`edl-chip${severityFilter.length === 0 ? ' edl-chip--on' : ''}`}
              aria-pressed={severityFilter.length === 0}
              onClick={() => setSeverityFilter([])}
              data-testid='edl-sev-ALL'
              title='Show every risk level'
            >
              All risk
            </button>
            {LEGEND.map((sev) => {
              const on = severityFilter.length === 0 || severityFilter.includes(sev);
              return (
                <button
                  key={sev}
                  type='button'
                  className={`edl-legend__toggle${on ? '' : ' edl-legend__toggle--off'}`}
                  aria-pressed={severityFilter.includes(sev)}
                  onClick={() => toggleSeverity(sev)}
                  data-testid={`edl-sev-${sev}`}
                  title={severityFilter.includes(sev) ? `Stop filtering on ${SEVERITY_STYLE[sev].label}` : `Show only ${SEVERITY_STYLE[sev].label}`}
                >
                  <span
                    className='edl-legend__swatch'
                    style={SEVERITY_STYLE[sev].dashed
                      ? { background: SEVERITY_STYLE[sev].bg, border: `1px dashed ${INK[300]}` }
                      : { background: SEVERITY_STYLE[sev].bg }}
                  />
                  {SEVERITY_STYLE[sev].label.toLowerCase()}
                </button>
              );
            })}
            {managedCount > 0 && (
              <button
                type='button'
                className={`edl-legend__toggle${managedOnly ? ' edl-legend__toggle--on' : ''}`}
                aria-pressed={managedOnly}
                onClick={() => setManagedOnly((v) => !v)}
                data-testid='edl-managed-key'
                title={managedOnly
                  ? 'Stop filtering on managed sources'
                  : `Show only the ${managedCount} source${managedCount === 1 ? '' : 's'} managed by hand (they sort last on the grid)`}
              >
                <span className='edl-legend__swatch edl-legend__swatch--managed' />
                managed
              </button>
            )}
          </div>
          <div className='edl-chips'>
            {CLASS_CHIPS.map(([key, label]) => {
              const Icon = CLASS_ICON[key];
              return (
                <button
                  key={key}
                  type='button'
                  className={`edl-chip${classFilter === key ? ' edl-chip--on' : ''}`}
                  onClick={() => setClassFilter(key)}
                  data-testid={`edl-class-${key}`}
                >
                  {Icon && <Icon size='12px' style={{ marginRight: 4, verticalAlign: -2 }} />}{label}
                </button>
              );
            })}
            <span className='edl-chip' style={{ marginLeft: 'auto', cursor: 'default' }}>Sized by: open fields</span>
          </div>
          {tiles.length === 0 ? (
            <div className='sg-small' data-testid='edl-empty'>
              {sources.length > 0
                ? 'No sources match the current risk and class filters.'
                : 'No data sources are assigned to the Enterprise Data Landscape yet. File-based sources land here by default; reclassify any source from its Data Source Risk Profile page.'}
            </div>
          ) : (
            <div className='edl-grid' data-testid='edl-grid'>
              {tiles.map(({ source, span }) => (
                <Tile key={source.id} source={source} span={span} onOpen={open} />
              ))}
            </div>
          )}
        </div>

        <div className='edl-card' data-testid='edl-queue'>
          <div className='d-flex align-items-center' style={{ marginBottom: 12, gap: 12 }}>
            <div className='sg-eyebrow'>Remediation queue</div>
            {fullQueue.length > QUEUE_SIZE && (
              <button type='button' className='edl-chip' style={{ marginLeft: 'auto' }}
                onClick={() => setShowAllQueue((v) => !v)} data-testid='edl-queue-toggle'>
                {showAllQueue ? `Show top ${QUEUE_SIZE}` : `Show all ${fullQueue.length}`}
              </button>
            )}
          </div>
          <div className='edl-queue'>
            {queue.length === 0 && <div className='sg-small'>Nothing open — no exposed sources.</div>}
            {queue.map((s) => {
              const count = queueCount(s);
              const width = queueMax ? Math.max(6, Math.round((count / queueMax) * 100)) : 0;
              const style = SEVERITY_STYLE[s.severity] || SEVERITY_STYLE.LOW;
              return (
                <button type='button' key={s.id} className='edl-queue__row' onClick={() => open(s)}
                  data-testid={`edl-queue-${s.id}`}>
                  <span className='edl-queue__name'>{s.name}</span>
                  <div className='edl-queue__track'>
                    <div className='edl-queue__bar' style={{ width: `${width}%`, background: style.bg }} />
                  </div>
                  <span className='edl-queue__count'>{count.toLocaleString()}</span>
                </button>
              );
            })}
            <div className='sg-small' style={{ marginTop: 3, color: INK[400] }}>
              Open fields — the columns, NoSQL fields and files still carrying a finding
              (persisting + new) · bar colour = source risk · click to open
            </div>
          </div>
        </div>

        <div className='sg-small' style={{ color: INK[400] }}>
          {Object.keys(DATA_CLASS_LABELS).length} data classes · {sources.length} source{sources.length === 1 ? '' : 's'} on this landscape
        </div>
      </div>
    </FetchContainer>
  );
};

export default Home;
